import { Injectable } from '@angular/core';
import { combineLatest, map, Observable, of, tap } from 'rxjs';
import {
  AdUSerDraftPostRequestModel,
  AdUserModel,
  AdUserPatchRequestModel,
  AdUserPostRequestModel,
  UserCountryAppService,
} from '../models/adUser.model';
import { ReferenceCountryModel } from '../../../shared/models/reference-country.model';
import { ApiListModel } from '../../../shared/models/api-list.model';
import { ListService } from '../../../shared/services/list.service';
import { DataService } from '../../../shared/interface/data-service';
import { DataServiceListParamsInfoModel } from '../../../shared/models/data-service.model';
import { ApiService } from '../../../shared/services/api.service';
import { ReferenceCountriesService } from '../../../shared/services/reference-countries.service';
import {
  AdUserDTOModel,
  AdUserUserCountryAppServiceDTOModel,
} from '../models/adUser.dto.model';
import { ReferenceAppServiceModel } from '../../../shared/models/reference-app-service.model';
import { ReferenceAppServicesService } from '../../../shared/services/reference-app-services.service';
import { UserProfileModel } from '../../../shared/models/user-profile.model';
import { MsalService } from '@azure/msal-angular';

@Injectable({
  providedIn: 'root',
})
export class UserManagementService
  extends ListService
  implements
    DataService<
      AdUserModel,
      AdUserPostRequestModel,
      AdUSerDraftPostRequestModel,
      AdUserPatchRequestModel
    >
{
  private _endpointUrl = 'users';

  constructor(
    public _apiService: ApiService,
    private referenceAppServicesService: ReferenceAppServicesService,
    private referenceCountriesService: ReferenceCountriesService,
    private authService: MsalService
  ) {
    super();
  }

  getList(
    dataServiceLisParamsInfo?: DataServiceListParamsInfoModel
  ): Observable<ApiListModel<AdUserModel>> {
    const params = this.getParams(dataServiceLisParamsInfo);

    this.loadingSubject.next(true);

    const referenceAppServiceList$ = this.referenceAppServicesService.getList();
    const referenceCountriesList$ =
      this.referenceCountriesService.getCountryList();

    const userListFromApi$ = this._apiService.get<ApiListModel<AdUserDTOModel>>(
      this._endpointUrl,
      params
    );

    return combineLatest([
      referenceAppServiceList$,
      referenceCountriesList$,
      userListFromApi$,
    ]).pipe(
      tap(
        ([
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          referenceAppServiceList$,
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          referenceCountriesList,
          userListFromApi,
        ]) => {
          this.totalCountSubject.next(userListFromApi.totalCount);
        }
      ),
      map(
        ([
          referenceAppServiceListFromApi,
          referenceCountriesListFromApi,
          userListFromApi,
        ]): ApiListModel<AdUserModel> => {
          return {
            ...userListFromApi,
            value: userListFromApi.value.map((userData: AdUserDTOModel) =>
              this.transformUserData(
                userData,
                referenceAppServiceListFromApi,
                referenceCountriesListFromApi
              )
            ),
          };
        }
      ),
      tap(() => {
        this.loadingSubject.next(false);
      })
    );
  }

  create(item: AdUserPostRequestModel): Observable<AdUserModel> {
    return this._apiService.post(this._endpointUrl, item as never);
  }

  update(id: string, item: AdUserPatchRequestModel): Observable<AdUserModel> {
    return this._apiService.put(`${this._endpointUrl}/${id}`, item as never);
  }

  getDetails(id: string): Observable<AdUserModel> {
    this.loadingSubject.next(true);

    const referenceAppServiceList$ = this.referenceAppServicesService.getList();
    const referenceCountriesList$ =
      this.referenceCountriesService.getCountryList();

    const userListFromApi$ = this._apiService.get<AdUserDTOModel>(
      `${this._endpointUrl}/${id}`
    );

    return combineLatest([
      referenceAppServiceList$,
      referenceCountriesList$,
      userListFromApi$,
    ]).pipe(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      map(
        ([
          referenceAppServiceListFromApi,
          referenceCountriesListFromApi,
          userFromApi,
        ]): AdUserModel => {
          return this.transformUserData(
            userFromApi,
            referenceAppServiceListFromApi,
            referenceCountriesListFromApi
          );
        }
      ),
      tap(() => {
        this.loadingSubject.next(false);
      })
    );
  }

  getCurrentUserDetails(): Observable<AdUserModel | undefined> {
    const activeAccount = this.authService.instance.getActiveAccount();

    if (activeAccount) {
      return this.getDetails(activeAccount.localAccountId);
    }

    return of(undefined);
  }

  delete(id: string): Observable<void> {
    return this._apiService.delete(`${this._endpointUrl}/${id}`);
  }

  batchDelete(ids: Array<string>): Observable<void> {
    return this._apiService.post(
      `${this._endpointUrl}/batch-delete`,
      ids as never
    );
  }

  private transformUserData(
    userData: AdUserDTOModel,
    appServiceList: ReferenceAppServiceModel[],
    countryList: ReferenceCountryModel[]
  ): AdUserModel {
    const appServiceListData = userData.appServicesId.map(
      (appServicesId: number) =>
        appServiceList.find(
          (appServiceInList: ReferenceAppServiceModel) =>
            appServicesId === appServiceInList.id
        )!
    );

    const countryListData = userData.countries.map(
      (countryCode: string) =>
        countryList.find(
          (countryInList: ReferenceCountryModel) =>
            countryCode === countryInList.countryCode
        )!
    );

    return {
      id: userData.adId,
      surName: userData.userInformation.surName,
      firstName: userData.userInformation.firstName,
      mail: userData.userInformation.userPrincipalName,
      activity: userData.userInformation.userPrincipalName,
      displayName:
        userData.userInformation.surName +
        ' ' +
        userData.userInformation.firstName,
      lastConnection: userData.lastConnectionDate
        ? new Date(userData.lastConnectionDate)
        : undefined,
      profileList: userData.userProfiles,
      profileListFormatted: this.getProfileListDisplayInList(
        userData.userProfiles
      ),
      appServiceList: appServiceListData,
      appServiceListFormatted:
        this.getAppServiceListDisplayInList(appServiceListData),
      countryList: countryListData,
      countryListFormatted: this.getCountryListDisplayInList(countryListData),
      userCountryAppServices: this.transformUserCountryAppServicesData(
        userData.userCountryAppServices
      ),
      isAdminAccount:
        userData.userProfiles.find((profile: UserProfileModel) => {
          return profile.id === 0 || profile.id === 1;
        }) !== undefined,
    };
  }

  private transformUserCountryAppServicesData(
    userCountryAppServicesData: AdUserUserCountryAppServiceDTOModel[]
  ): UserCountryAppService[] {
    return userCountryAppServicesData.map(
      (
        userCountryAppServicesInList: AdUserUserCountryAppServiceDTOModel
      ): UserCountryAppService => {
        return {
          userId: userCountryAppServicesInList.userId,
          countryCode: userCountryAppServicesInList.countryCode,
          appServiceId: userCountryAppServicesInList.appServiceId,
          signature: userCountryAppServicesInList.signature,
        };
      }
    );
  }

  private getProfileListDisplayInList(profileList: UserProfileModel[]): string {
    const profileListName: Array<string> = profileList.map(
      (profile: UserProfileModel) => profile.name
    );

    let profileListDisplay = profileListName.slice(0, 3).join(', ');

    if (profileListName.length > 3) {
      profileListDisplay = `${profileListDisplay}... (+${profileListName.length - 3})`;
    }

    return profileListDisplay;
  }

  private getAppServiceListDisplayInList(
    appServiceList: ReferenceAppServiceModel[]
  ): string {
    const appServiceListName: Array<string> = appServiceList.map(
      (appService: ReferenceAppServiceModel) => appService.name
    );

    let profileListDisplay = appServiceListName.slice(0, 3).join(', ');

    if (appServiceListName.length > 3) {
      profileListDisplay = `${profileListDisplay}... (+${appServiceListName.length - 3})`;
    }

    return profileListDisplay;
  }

  private getCountryListDisplayInList(
    countryList: ReferenceCountryModel[]
  ): string {
    const countryListName: Array<string> = countryList
      .filter((countryInList: ReferenceCountryModel) => countryInList)
      .map((country: ReferenceCountryModel) => country.name);

    let countryListDisplay = countryListName.slice(0, 3).join(', ');

    if (countryListName.length > 3) {
      countryListDisplay = `${countryListDisplay}... (+${countryListName.length - 3})`;
    }

    return countryListDisplay;
  }
}
